/**
 * Hook para gerenciar histórico unificado via timeline
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { HistoricoService } from "../services/historicoService";
import {
	normalizeTimelineResponse,
	filterBySearchTerm,
} from "../utils/historicoNormalizer";
import type {
	HistoricoItem,
	HistoricoFilters,
	HistoricoPagination,
	TimelineSourceFilter,
	TimelineSourcesCount,
} from "../types/historico";
import { storage } from "../lib/storage/storage";
import { getLastThirtyDaysRange } from "../utils/date";
import { logger } from "../lib/terminalLogger";

const DEFAULT_LIMIT = 20;
const STORAGE_KEY = "historico-filters";

interface UseHistoricoReturn {
	items: HistoricoItem[];
	loading: boolean;
	error: string | null;
	pagination: HistoricoPagination;
	filters: HistoricoFilters;
	sourcesCount: TimelineSourcesCount | null;
	setFilters: (
		updater:
			| ((prev: HistoricoFilters) => HistoricoFilters)
			| HistoricoFilters
	) => void;
	loadMore: () => Promise<void>;
	refresh: () => Promise<void>;
	hasMore: boolean;
}

export function useHistorico(
	initialSource: TimelineSourceFilter = "all"
): UseHistoricoReturn {
	// Calcular últimos 30 dias como padrão
	const defaultDateRange = getLastThirtyDaysRange();

	// Estado de filtros com persistência
	const [filters, setFiltersState] = useState<HistoricoFilters>(() => {
		try {
			const saved = storage.get<HistoricoFilters>(STORAGE_KEY, {
				area: "local",
			});
			// Se não houver datas salvas, usar padrão dos últimos 30 dias
			const hasDateFilter = saved?.dateFrom && saved?.dateTo;
			// Determinar source: usar salvo se existir, senão initialSource
			const effectiveSource = saved?.source || initialSource;
			return {
				page: 1,
				limit: DEFAULT_LIMIT,
				// Aplicar últimos 30 dias como padrão se não houver filtro de data salvo
				...(hasDateFilter ? {} : defaultDateRange),
				...saved,
				// Source definido por último para garantir prioridade correta
				source: effectiveSource,
			};
		} catch {
			return {
				page: 1,
				limit: DEFAULT_LIMIT,
				source: initialSource,
				// Aplicar últimos 30 dias como padrão
				...defaultDateRange,
			};
		}
	});

	const [items, setItems] = useState<HistoricoItem[]>([]);
	const [allItems, setAllItems] = useState<HistoricoItem[]>([]); // Para busca client-side
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [sourcesCount, setSourcesCount] =
		useState<TimelineSourcesCount | null>(null);

	// Paginação
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [totalItems, setTotalItems] = useState(0);

	// Persistir filtros no localStorage
	useEffect(() => {
		try {
			storage.set(STORAGE_KEY, filters, { area: "local" });
		} catch {}
	}, [filters]);

	const setFilters = useCallback(
		(
			updater:
				| ((prev: HistoricoFilters) => HistoricoFilters)
				| HistoricoFilters
		) => {
			setFiltersState((prev) => {
				const next =
					typeof updater === "function" ? updater(prev) : updater;
				const prevSource = prev.source;
				const nextSource = next.source;

				// Se mudou de source ou datas, resetar página
				if (
					prevSource !== nextSource ||
					prev.dateFrom !== next.dateFrom ||
					prev.dateTo !== next.dateTo
				) {
					setCurrentPage(1);
					setAllItems([]);
				}

				return { ...next, page: 1 };
			});
		},
		[]
	);

	// Buscar dados da API
	const fetchData = useCallback(
		async (isLoadMore = false) => {
			setLoading(true);
			setError(null);

			const page = isLoadMore ? currentPage + 1 : 1;

			try {
				const response = await HistoricoService.fetchTimeline({
					source: filters.source || "all",
					page,
					limit: filters.limit || DEFAULT_LIMIT,
					date_from: filters.dateFrom,
					date_to: filters.dateTo,
					// Não enviar searchTerm para API - busca é client-side
				});

				if (!response.success) {
					throw new Error(response.error || "Erro ao buscar histórico");
				}

				const normalizedItems = normalizeTimelineResponse(response);

				// Atualizar contagens
				setSourcesCount(response.data.sources_count);
				setTotalPages(response.data.pagination.total_pages);
				setTotalItems(response.data.pagination.total_items);
				setCurrentPage(page);

				if (isLoadMore) {
					// Adicionar novos itens aos existentes
					setAllItems((prev) => [...prev, ...normalizedItems]);
				} else {
					// Substituir itens
					setAllItems(normalizedItems);
				}
			} catch (err: any) {
				logger.error("[Historico] Erro ao buscar timeline:", err);
				const errorMsg =
					err?.message ||
					err?.data?.error ||
					"Erro ao buscar histórico";
				setError(errorMsg);
			} finally {
				setLoading(false);
			}
		},
		[filters.source, filters.limit, filters.dateFrom, filters.dateTo, currentPage]
	);

	// Aplicar filtro de busca client-side
	useEffect(() => {
		if (filters.searchTerm && filters.searchTerm.trim()) {
			const filtered = filterBySearchTerm(allItems, filters.searchTerm);
			setItems(filtered);
		} else {
			setItems(allItems);
		}
	}, [allItems, filters.searchTerm]);

	// Carregar mais (página seguinte)
	const loadMore = useCallback(async () => {
		if (loading || currentPage >= totalPages) return;
		await fetchData(true);
	}, [loading, currentPage, totalPages, fetchData]);

	// Refresh completo
	const refresh = useCallback(async () => {
		setCurrentPage(1);
		setAllItems([]);
		await fetchData(false);
	}, [fetchData]);

	// Paginação calculada
	const pagination = useMemo<HistoricoPagination>(
		() => ({
			page: currentPage,
			limit: filters.limit || DEFAULT_LIMIT,
			total: totalItems,
			totalPages,
		}),
		[currentPage, filters.limit, totalItems, totalPages]
	);

	// Verificar se há mais itens
	const hasMore = useMemo(() => {
		return currentPage < totalPages;
	}, [currentPage, totalPages]);

	// Carregar dados iniciais e quando filtros mudarem
	useEffect(() => {
		setCurrentPage(1);
		setAllItems([]);
		fetchData(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters.source, filters.dateFrom, filters.dateTo]);

	return {
		items,
		loading,
		error,
		pagination,
		filters,
		sourcesCount,
		setFilters,
		loadMore,
		refresh,
		hasMore,
	};
}
